import { useState, useEffect, useMemo } from 'react'
import {
  ArrowLeft, Building2, Download, Loader2, Calendar,
  Filter, Save, Printer, AlertTriangle, RefreshCw, Info,
  Users, UserCheck, UserX, FileText, X,
} from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { fetchAllIn } from '../../lib/fetchAll'
import { generateCommuneVerificationPdf, type PdfWeekSection } from '../../lib/communeBillingPdf'
import { sansJpa, stagesDesJpa } from '../../../lib/jpa/stages.supabase'

interface Props {
  onBack: () => void
}

interface Center { id: string; name: string }
interface SessionRow {
  id: string
  periode: string
  semaine: string
  start_date: string
  end_date: string
  center_id: string
  nombre_jours: number
}
interface RegistrationRow {
  id: string
  kid_id: string
  activity_id: string
  price_type: string
  cancellation_status: string | null
}
interface PresenceRow {
  registration_id: string
  monday_present: boolean | null
  tuesday_present: boolean | null
  wednesday_present: boolean | null
  thursday_present: boolean | null
  friday_present: boolean | null
}
interface KidRow {
  id: string
  user_id: string
  nom: string
  prenom: string
  ecole: string | null
}
interface SchoolRow { id: string; name: string }
interface ParentRow { id: string; cpostal: string | null }

interface SavedBillingRow {
  id: string
  center_id: string
  year: number
  periode: string
  semaine: string
  nombre_jours: number
  kids_local: number
  kids_local_reduced: number
  rate_local: number
  rate_local_reduced: number
  advance_amount: number
  notes: string | null
}

interface ChildRow {
  kidId: string
  nom: string
  prenom: string
  priceType: 'local' | 'local_reduced'
  cancellationStatus: string | null
  days: { L: boolean; M: boolean; Me: boolean; J: boolean; V: boolean }
  hasAnyPresence: boolean
  schoolLabel: string | null
  parentCpostal: string | null
}

interface WeekDetail {
  semaine: string
  startDate: string
  endDate: string
  nombreJours: number
  counted: ChildRow[]
  excluded: Array<{ row: ChildRow; reason: string }>
  stats: {
    localRegsTotal: number
    localKidsRetained: number
    localKidsNoPresence: number
    localFromCancelledRetained: number
    localReducedRegsTotal: number
    localReducedKidsRetained: number
    localReducedKidsNoPresence: number
    localReducedFromCancelledRetained: number
  }
}

interface WeekBillingData {
  semaine: string
  startDate: string
  endDate: string
  nombreJours: number
  kidsLocal: number
  kidsLocalReduced: number
  rateLocal: number
  rateLocalReduced: number
  notes: string
}

const CANCELLED_STATUSES = new Set(['cancelled_full_refund', 'cancelled_no_refund', 'cancelled_partial_refund', 'requested'])

function getWeekDays(startDate: string, endDate: string): string[] {
  const days: string[] = []
  const current = new Date(startDate + 'T00:00:00')
  const end = new Date(endDate + 'T00:00:00')
  while (current <= end) {
    const dow = current.getDay()
    if (dow >= 1 && dow <= 5) days.push(current.toISOString().slice(0, 10))
    current.setDate(current.getDate() + 1)
  }
  return days
}
function formatDate(d: string) { const [y, m, day] = d.split('-'); return `${day}/${m}/${y}` }
function formatCurrency(n: number) { return n.toLocaleString('fr-BE', { style: 'currency', currency: 'EUR' }) }

const isUuid = (s: string | null | undefined): boolean =>
  !!s && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s)

export function CommuneBilling({ onBack }: Props) {
  const [centers, setCenters] = useState<Center[]>([])
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString())
  const [selectedCenter, setSelectedCenter] = useState('')
  const [selectedPeriod, setSelectedPeriod] = useState('')
  const [availableYears, setAvailableYears] = useState<string[]>([])
  const [availablePeriods, setAvailablePeriods] = useState<string[]>([])

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [generated, setGenerated] = useState(false)

  const [weeks, setWeeks] = useState<WeekBillingData[]>([])
  const [weekDetails, setWeekDetails] = useState<WeekDetail[]>([])
  const [advanceAmount, setAdvanceAmount] = useState(0)
  const [hasChanges, setHasChanges] = useState(false)

  const [openTooltip, setOpenTooltip] = useState<string | null>(null)
  const [modalState, setModalState] = useState<{ semaine: string; kind: 'counted' | 'excluded' } | null>(null)

  useEffect(() => {
    supabase.from('centers').select('id, name').eq('active', true).order('name')
      .then(({ data }) => setCenters(data || []))
  }, [])

  useEffect(() => {
    stagesDesJpa().catch(() => [] as string[]).then((jpa) => sansJpa(supabase.from('sessions').select('start_date'), jpa)).then(({ data }) => {
      if (data) {
        const years = [...new Set(data.map(s => new Date(s.start_date).getFullYear().toString()))].sort().reverse()
        setAvailableYears(years)
      }
    })
  }, [])

  useEffect(() => {
    setSelectedPeriod('')
    if (!selectedYear || !selectedCenter) { setAvailablePeriods([]); return }
    stagesDesJpa().catch(() => [] as string[]).then((jpa) => sansJpa(supabase
      .from('sessions')
      .select('periode')
      .eq('center_id', selectedCenter)
      .gte('start_date', `${selectedYear}-01-01`)
      .lte('start_date', `${selectedYear}-12-31`), jpa))
      .then(({ data }) => {
        if (data) setAvailablePeriods([...new Set(data.map(s => s.periode))].sort())
      })
  }, [selectedYear, selectedCenter])

  useEffect(() => {
    setGenerated(false)
    setWeeks([])
    setWeekDetails([])
    setAdvanceAmount(0)
    setHasChanges(false)
  }, [selectedYear, selectedCenter, selectedPeriod])

  const handleGenerate = async () => {
    if (!selectedYear || !selectedPeriod || !selectedCenter) return
    setLoading(true)
    setGenerated(false)

    try {
      const { data: sessionsData } = await sansJpa(supabase
        .from('sessions')
        .select('id, periode, semaine, start_date, end_date, center_id, nombre_jours')
        .eq('center_id', selectedCenter)
        .eq('periode', selectedPeriod)
        .gte('start_date', `${selectedYear}-01-01`)
        .lte('start_date', `${selectedYear}-12-31`)
        .order('start_date'), await stagesDesJpa())

      if (!sessionsData || sessionsData.length === 0) {
        setWeeks([])
        setWeekDetails([])
        setGenerated(true)
        setLoading(false)
        return
      }

      const weekMap = new Map<string, { startDate: string; endDate: string; sessionIds: string[] }>()
      for (const s of sessionsData) {
        const existing = weekMap.get(s.semaine)
        if (existing) {
          existing.sessionIds.push(s.id)
          if (s.start_date < existing.startDate) existing.startDate = s.start_date
          if (s.end_date > existing.endDate) existing.endDate = s.end_date
        } else {
          weekMap.set(s.semaine, { startDate: s.start_date, endDate: s.end_date, sessionIds: [s.id] })
        }
      }

      const allStartDates = sessionsData.map(s => s.start_date)
      const allEndDates = sessionsData.map(s => s.end_date)
      const minDate = allStartDates.reduce((a, b) => a < b ? a : b)
      const maxDate = allEndDates.reduce((a, b) => a > b ? a : b)

      const { data: holidaysData } = await supabase
        .from('public_holidays').select('date').gte('date', minDate).lte('date', maxDate)
      const holidayDates = new Set((holidaysData || []).map(h => h.date))

      const allSessionIds = sessionsData.map(s => s.id)
      const allRegs = await fetchAllIn<RegistrationRow>(
        'registrations',
        'id, kid_id, activity_id, price_type, cancellation_status',
        'activity_id',
        allSessionIds,
      )

      const localRegs = allRegs.filter(r => {
        const pt = (r.price_type || '').trim()
        return pt === 'local' || pt === 'local_reduced'
      })

      const regIds = localRegs.map(r => r.id)
      const allPresences: PresenceRow[] = regIds.length > 0
        ? await fetchAllIn<PresenceRow>(
            'presences',
            'registration_id, monday_present, tuesday_present, wednesday_present, thursday_present, friday_present',
            'registration_id',
            regIds,
          )
        : []
      const presenceMap = new Map<string, PresenceRow>()
      for (const p of allPresences) presenceMap.set(p.registration_id, p)

      const kidIds = [...new Set(localRegs.map(r => r.kid_id))]
      const kidsRows = kidIds.length > 0
        ? await fetchAllIn<KidRow>('kids', 'id, user_id, nom, prenom, ecole', 'id', kidIds)
        : []
      const kidById = new Map(kidsRows.map(k => [k.id, k]))

      const schoolIds = [...new Set(kidsRows.map(k => k.ecole).filter(v => isUuid(v)) as string[])]
      const schoolRows = schoolIds.length > 0
        ? await fetchAllIn<SchoolRow>('schools', 'id, name', 'id', schoolIds)
        : []
      const schoolById = new Map(schoolRows.map(s => [s.id, s.name]))

      const parentIds = [...new Set(kidsRows.map(k => k.user_id).filter(Boolean))]
      const parentRows = parentIds.length > 0
        ? await fetchAllIn<ParentRow>('users', 'id, cpostal', 'id', parentIds)
        : []
      const parentById = new Map(parentRows.map(p => [p.id, p.cpostal || null]))

      const { data: savedRows } = await supabase
        .from('commune_billing').select('*')
        .eq('center_id', selectedCenter).eq('year', parseInt(selectedYear)).eq('periode', selectedPeriod)
      const savedMap = new Map<string, SavedBillingRow>((savedRows || []).map(r => [r.semaine, r]))

      const sortedWeeks = [...weekMap.entries()].sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }))

      const weekBilling: WeekBillingData[] = []
      const weekDetailList: WeekDetail[] = []

      for (const [semaine, weekInfo] of sortedWeeks) {
        const weekDays = getWeekDays(weekInfo.startDate, weekInfo.endDate)
        const workingDays = weekDays.filter(d => !holidayDates.has(d))
        const nombreJours = workingDays.length

        const weekSessionIds = new Set(weekInfo.sessionIds)
        const weekRegs = localRegs.filter(r => weekSessionIds.has(r.activity_id))

        // Per-kid aggregation: (kid_id, priceType) → aggregated ChildRow
        type Agg = {
          kidId: string
          priceType: 'local' | 'local_reduced'
          regs: RegistrationRow[]
          days: { L: boolean; M: boolean; Me: boolean; J: boolean; V: boolean }
          anyPresence: boolean
          anyNonCancelled: boolean
          anyCancelled: boolean
        }
        const aggByKey = new Map<string, Agg>()
        for (const reg of weekRegs) {
          const pt = (reg.price_type.trim()) as 'local' | 'local_reduced'
          const key = `${reg.kid_id}::${pt}`
          let a = aggByKey.get(key)
          if (!a) {
            a = {
              kidId: reg.kid_id,
              priceType: pt,
              regs: [],
              days: { L: false, M: false, Me: false, J: false, V: false },
              anyPresence: false,
              anyNonCancelled: false,
              anyCancelled: false,
            }
            aggByKey.set(key, a)
          }
          a.regs.push(reg)
          const p = presenceMap.get(reg.id)
          if (p) {
            if (p.monday_present === true) a.days.L = true
            if (p.tuesday_present === true) a.days.M = true
            if (p.wednesday_present === true) a.days.Me = true
            if (p.thursday_present === true) a.days.J = true
            if (p.friday_present === true) a.days.V = true
            if (a.days.L || a.days.M || a.days.Me || a.days.J || a.days.V) a.anyPresence = true
          }
          const cs = (reg.cancellation_status || '').trim()
          if (CANCELLED_STATUSES.has(cs)) a.anyCancelled = true
          else a.anyNonCancelled = true
        }

        const counted: ChildRow[] = []
        const excluded: Array<{ row: ChildRow; reason: string }> = []
        let localRegsTotal = 0, localReducedRegsTotal = 0
        let localKidsRetained = 0, localReducedKidsRetained = 0
        let localKidsNoPresence = 0, localReducedKidsNoPresence = 0
        let localFromCancelledRetained = 0, localReducedFromCancelledRetained = 0

        for (const a of aggByKey.values()) {
          if (a.priceType === 'local') localRegsTotal += a.regs.length
          else localReducedRegsTotal += a.regs.length

          const kid = kidById.get(a.kidId)
          const nom = kid?.nom || '(inconnu)'
          const prenom = kid?.prenom || ''
          const ecole = kid?.ecole || null
          let schoolLabel: string | null = null
          if (isUuid(ecole)) {
            schoolLabel = schoolById.get(ecole!) || null
          } else if (ecole && ecole.trim() && ecole.trim().toLowerCase() !== 'other') {
            schoolLabel = ecole.trim()
          }
          const parentCpostal = kid?.user_id ? (parentById.get(kid.user_id) || null) : null
          const cancellationStatus = a.anyNonCancelled ? 'none' : (a.regs[0]?.cancellation_status || null)

          const row: ChildRow = {
            kidId: a.kidId,
            nom,
            prenom,
            priceType: a.priceType,
            cancellationStatus,
            days: a.days,
            hasAnyPresence: a.anyPresence,
            schoolLabel,
            parentCpostal,
          }

          if (a.anyPresence) {
            counted.push(row)
            if (a.priceType === 'local') {
              localKidsRetained += 1
              if (!a.anyNonCancelled) localFromCancelledRetained += 1
            } else {
              localReducedKidsRetained += 1
              if (!a.anyNonCancelled) localReducedFromCancelledRetained += 1
            }
          } else {
            const isCancelled = !a.anyNonCancelled
            const reason = isCancelled
              ? 'Inscription annulee et aucune presence enregistree cette semaine'
              : 'Aucun jour de presence coche pour cette semaine'
            excluded.push({ row, reason })
            if (a.priceType === 'local') localKidsNoPresence += 1
            else localReducedKidsNoPresence += 1
          }
        }

        counted.sort((a, b) => (a.nom || '').localeCompare(b.nom || '') || (a.prenom || '').localeCompare(b.prenom || ''))
        excluded.sort((a, b) => (a.row.nom || '').localeCompare(b.row.nom || ''))

        const kidsLocal = counted.filter(c => c.priceType === 'local').length
        const kidsLocalReduced = counted.filter(c => c.priceType === 'local_reduced').length

        const saved = savedMap.get(semaine)
        weekBilling.push({
          semaine,
          startDate: weekInfo.startDate,
          endDate: weekInfo.endDate,
          nombreJours,
          kidsLocal,
          kidsLocalReduced,
          rateLocal: saved?.rate_local ?? 0,
          rateLocalReduced: saved?.rate_local_reduced ?? 0,
          notes: saved?.notes ?? '',
        })
        weekDetailList.push({
          semaine,
          startDate: weekInfo.startDate,
          endDate: weekInfo.endDate,
          nombreJours,
          counted,
          excluded,
          stats: {
            localRegsTotal,
            localKidsRetained,
            localKidsNoPresence,
            localFromCancelledRetained,
            localReducedRegsTotal,
            localReducedKidsRetained,
            localReducedKidsNoPresence,
            localReducedFromCancelledRetained,
          },
        })
      }

      setWeeks(weekBilling)
      setWeekDetails(weekDetailList)
      const firstSaved = savedMap.get(sortedWeeks[0]?.[0] ?? '')
      setAdvanceAmount(firstSaved?.advance_amount ?? 0)
      setGenerated(true)
      setHasChanges(false)
    } catch (err) {
      console.error('Error generating commune billing:', err)
    } finally {
      setLoading(false)
    }
  }

  const updateWeekRate = (index: number, field: 'rateLocal' | 'rateLocalReduced', value: number) => {
    setWeeks(prev => { const u = [...prev]; u[index] = { ...u[index], [field]: value }; return u })
    setHasChanges(true)
  }
  const updateWeekNotes = (index: number, value: string) => {
    setWeeks(prev => { const u = [...prev]; u[index] = { ...u[index], notes: value }; return u })
    setHasChanges(true)
  }

  const totals = useMemo(() => {
    let totalLocal = 0, totalLocalReduced = 0, totalKidsLocal = 0, totalKidsLocalReduced = 0
    for (const w of weeks) {
      totalLocal += w.kidsLocal * w.rateLocal
      totalLocalReduced += w.kidsLocalReduced * w.rateLocalReduced
      totalKidsLocal += w.kidsLocal
      totalKidsLocalReduced += w.kidsLocalReduced
    }
    const grandTotal = totalLocal + totalLocalReduced
    return { totalLocal, totalLocalReduced, grandTotal, solde: grandTotal - advanceAmount, totalKidsLocal, totalKidsLocalReduced }
  }, [weeks, advanceAmount])

  const handleSave = async () => {
    if (!selectedCenter || !selectedYear || !selectedPeriod || weeks.length === 0) return
    setSaving(true)
    try {
      const rows = weeks.map((w, i) => ({
        center_id: selectedCenter,
        year: parseInt(selectedYear),
        periode: selectedPeriod,
        semaine: w.semaine,
        nombre_jours: w.nombreJours,
        kids_local: w.kidsLocal,
        kids_local_reduced: w.kidsLocalReduced,
        rate_local: w.rateLocal,
        rate_local_reduced: w.rateLocalReduced,
        advance_amount: i === 0 ? advanceAmount : 0,
        notes: w.notes || null,
        updated_at: new Date().toISOString(),
      }))
      const { error } = await supabase
        .from('commune_billing')
        .upsert(rows, { onConflict: 'center_id,year,periode,semaine' })
      if (error) throw error
      setHasChanges(false)
    } catch (err) {
      console.error('Error saving commune billing:', err)
      alert('Erreur lors de la sauvegarde. Veuillez reessayer.')
    } finally {
      setSaving(false)
    }
  }

  const handleExportCSV = () => {
    const centerName = centers.find(c => c.id === selectedCenter)?.name || 'Centre'
    const headers = ['Semaine', 'Dates', 'Jours', 'Enfants local', 'Tarif local', 'Sous-total local', 'Enfants local reduit', 'Tarif local reduit', 'Sous-total local reduit', 'Total semaine', 'Notes']
    const csvRows = [headers.join(';')]
    for (const w of weeks) {
      const subLocal = w.kidsLocal * w.rateLocal
      const subReduced = w.kidsLocalReduced * w.rateLocalReduced
      csvRows.push([
        w.semaine,
        `${formatDate(w.startDate)} - ${formatDate(w.endDate)}`,
        w.nombreJours.toString(),
        w.kidsLocal.toString(),
        w.rateLocal.toFixed(2),
        subLocal.toFixed(2),
        w.kidsLocalReduced.toString(),
        w.rateLocalReduced.toFixed(2),
        subReduced.toFixed(2),
        (subLocal + subReduced).toFixed(2),
        `"${(w.notes || '').replace(/"/g, '""')}"`,
      ].join(';'))
    }
    csvRows.push('')
    csvRows.push(`Total general;;;;;;;;;;${totals.grandTotal.toFixed(2)}`)
    csvRows.push(`Avances recues;;;;;;;;;;${advanceAmount.toFixed(2)}`)
    csvRows.push(`Solde a facturer;;;;;;;;;;${totals.solde.toFixed(2)}`)
    const blob = new Blob(['\uFEFF' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `facturation-commune-${centerName}-${selectedPeriod}-${selectedYear}.csv`; a.click()
    URL.revokeObjectURL(url)
  }

  const handleDownloadPdf = async () => {
    if (weekDetails.length === 0) return
    setPdfLoading(true)
    try {
      const centerName = centers.find(c => c.id === selectedCenter)?.name || 'Centre'
      const sections: PdfWeekSection[] = weekDetails.map(wd => ({
        semaine: wd.semaine,
        startDate: wd.startDate,
        endDate: wd.endDate,
        children: wd.counted.map(c => ({
          nom: c.nom,
          prenom: c.prenom,
          priceType: c.priceType,
          schoolName: c.schoolLabel,
          parentCpostal: c.parentCpostal,
        })),
      }))
      const bytes = await generateCommuneVerificationPdf({
        centerName, periode: selectedPeriod, year: selectedYear, sections,
      })
      const blob = new Blob([bytes], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const safe = centerName.replace(/[^a-zA-Z0-9]+/g, '-')
      const a = document.createElement('a')
      a.href = url; a.download = `verification-commune-${safe}-${selectedPeriod}-${selectedYear}.pdf`; a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('PDF generation failed:', err)
      alert('Impossible de generer le PDF. Merci de reessayer.')
    } finally {
      setPdfLoading(false)
    }
  }

  const handlePrint = () => window.print()
  const selectedCenterName = centers.find(c => c.id === selectedCenter)?.name

  const modalDetail = modalState ? weekDetails.find(d => d.semaine === modalState.semaine) : null

  return (
    <div className="space-y-6 print:space-y-4">
      <div className="flex items-center justify-between print:hidden">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Facturation aux communes</h1>
            <p className="text-gray-600">Calcul des montants pour les tarifs locaux et sociaux locaux</p>
          </div>
        </div>
      </div>

      <div className="hidden print:block">
        <h1 className="text-xl font-bold text-gray-900">Facturation commune - {selectedCenterName}</h1>
        <p className="text-sm text-gray-600">{selectedPeriod} {selectedYear}</p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 print:hidden">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-5 w-5 text-gray-500" />
          <h2 className="font-semibold text-gray-900">Filtres</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Annee</label>
            <select value={selectedYear} onChange={e => setSelectedYear(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500">
              <option value="">Selectionner</option>
              {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Centre</label>
            <select value={selectedCenter} onChange={e => setSelectedCenter(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500">
              <option value="">Selectionner</option>
              {centers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Periode</label>
            <select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)} disabled={!selectedCenter || !selectedYear} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500">
              <option value="">Selectionner</option>
              {availablePeriods.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-3">
          <button onClick={handleGenerate} disabled={!selectedYear || !selectedPeriod || !selectedCenter || loading} className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed">
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Calcul en cours...</> : <><RefreshCw className="h-4 w-4" /> Generer / Recalculer</>}
          </button>
          {generated && weeks.length > 0 && (
            <>
              <button onClick={handleSave} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50">
                {saving ? <><Loader2 className="h-4 w-4 animate-spin" /> Sauvegarde...</> : <><Save className="h-4 w-4" /> Sauvegarder</>}
              </button>
              <button onClick={handleExportCSV} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium">
                <Download className="h-4 w-4" /> CSV
              </button>
              <button onClick={handleDownloadPdf} disabled={pdfLoading} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50">
                {pdfLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                PDF de verification
              </button>
              <button onClick={handlePrint} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium">
                <Printer className="h-4 w-4" /> Imprimer
              </button>
            </>
          )}
        </div>
      </div>

      {hasChanges && generated && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-amber-800 text-sm print:hidden">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>Des modifications n'ont pas ete sauvegardees. Pensez a sauvegarder avant de quitter.</span>
        </div>
      )}

      {generated && (
        <>
          {weeks.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aucune session trouvee pour cette selection.</p>
            </div>
          ) : (
            <>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Semaine</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Dates</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Jours</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider bg-teal-50">Enfants local</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider bg-teal-50 print:hidden">Tarif local</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider bg-teal-50">Sous-total local</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider bg-sky-50">Enfants social local</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider bg-sky-50 print:hidden">Tarif social local</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider bg-sky-50">Sous-total social</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {weeks.map((w, i) => {
                        const detail = weekDetails[i]
                        const subLocal = w.kidsLocal * w.rateLocal
                        const subReduced = w.kidsLocalReduced * w.rateLocalReduced
                        return (
                          <tr key={w.semaine} className="hover:bg-gray-50 transition-colors">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{w.semaine}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(w.startDate)} - {formatDate(w.endDate)}</td>
                            <td className="px-4 py-3 text-sm text-center">
                              <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold ${w.nombreJours < 5 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'}`}>{w.nombreJours}</span>
                            </td>
                            <td className="px-4 py-3 text-sm text-center font-medium bg-teal-50/30">
                              <span className="inline-flex items-center gap-1">
                                {w.kidsLocal}
                                {detail && (
                                  <BreakdownIcon
                                    id={`local-${w.semaine}`}
                                    openTooltip={openTooltip}
                                    setOpenTooltip={setOpenTooltip}
                                    tone="teal"
                                    title="Detail tarif local"
                                    stats={{
                                      totalRegs: detail.stats.localRegsTotal,
                                      retained: detail.stats.localKidsRetained,
                                      noPresence: detail.stats.localKidsNoPresence,
                                      cancelledButPresent: detail.stats.localFromCancelledRetained,
                                    }}
                                  />
                                )}
                              </span>
                            </td>
                            <td className="px-4 py-3 bg-teal-50/30 print:hidden">
                              <input type="number" min="0" step="0.01" value={w.rateLocal || ''} onChange={e => updateWeekRate(i, 'rateLocal', parseFloat(e.target.value) || 0)} className="w-24 rounded-md border border-gray-300 px-2 py-1 text-sm text-right focus:ring-2 focus:ring-teal-500 focus:border-teal-500" placeholder="0.00" />
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-medium bg-teal-50/30">{formatCurrency(subLocal)}</td>
                            <td className="px-4 py-3 text-sm text-center font-medium bg-sky-50/30">
                              <span className="inline-flex items-center gap-1">
                                {w.kidsLocalReduced}
                                {detail && (
                                  <BreakdownIcon
                                    id={`reduced-${w.semaine}`}
                                    openTooltip={openTooltip}
                                    setOpenTooltip={setOpenTooltip}
                                    tone="sky"
                                    title="Detail tarif local reduit"
                                    stats={{
                                      totalRegs: detail.stats.localReducedRegsTotal,
                                      retained: detail.stats.localReducedKidsRetained,
                                      noPresence: detail.stats.localReducedKidsNoPresence,
                                      cancelledButPresent: detail.stats.localReducedFromCancelledRetained,
                                    }}
                                  />
                                )}
                              </span>
                            </td>
                            <td className="px-4 py-3 bg-sky-50/30 print:hidden">
                              <input type="number" min="0" step="0.01" value={w.rateLocalReduced || ''} onChange={e => updateWeekRate(i, 'rateLocalReduced', parseFloat(e.target.value) || 0)} className="w-24 rounded-md border border-gray-300 px-2 py-1 text-sm text-right focus:ring-2 focus:ring-sky-500 focus:border-sky-500" placeholder="0.00" />
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-medium bg-sky-50/30">{formatCurrency(subReduced)}</td>
                            <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">{formatCurrency(subLocal + subReduced)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                    <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                      <tr>
                        <td colSpan={3} className="px-4 py-3 text-sm font-bold text-gray-900">Total</td>
                        <td className="px-4 py-3 text-sm text-center font-bold bg-teal-50/30">{totals.totalKidsLocal}</td>
                        <td className="px-4 py-3 bg-teal-50/30 print:hidden"></td>
                        <td className="px-4 py-3 text-sm text-right font-bold bg-teal-50/30">{formatCurrency(totals.totalLocal)}</td>
                        <td className="px-4 py-3 text-sm text-center font-bold bg-sky-50/30">{totals.totalKidsLocalReduced}</td>
                        <td className="px-4 py-3 bg-sky-50/30 print:hidden"></td>
                        <td className="px-4 py-3 text-sm text-right font-bold bg-sky-50/30">{formatCurrency(totals.totalLocalReduced)}</td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">{formatCurrency(totals.grandTotal)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 print:hidden">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Users className="h-4 w-4 text-gray-500" />
                  Verification par semaine
                </h3>
                <div className="space-y-2">
                  {weekDetails.map(wd => (
                    <div key={wd.semaine} className="flex flex-wrap items-center gap-2 text-sm border border-gray-100 rounded-lg px-3 py-2">
                      <span className="font-medium text-gray-900 w-14">{wd.semaine}</span>
                      <span className="text-gray-500">{formatDate(wd.startDate)} - {formatDate(wd.endDate)}</span>
                      <span className="text-gray-700">
                        {wd.counted.length} enfant{wd.counted.length > 1 ? 's' : ''} compte{wd.counted.length > 1 ? 's' : ''}
                        {wd.excluded.length > 0 && <> - {wd.excluded.length} exclu{wd.excluded.length > 1 ? 's' : ''}</>}
                      </span>
                      <div className="ml-auto flex gap-2">
                        <button onClick={() => setModalState({ semaine: wd.semaine, kind: 'counted' })} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-teal-50 text-teal-700 rounded-md hover:bg-teal-100 text-xs font-medium">
                          <UserCheck className="h-3.5 w-3.5" /> Voir les enfants comptes
                        </button>
                        <button onClick={() => setModalState({ semaine: wd.semaine, kind: 'excluded' })} disabled={wd.excluded.length === 0} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-50 text-red-700 rounded-md hover:bg-red-100 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed">
                          <UserX className="h-3.5 w-3.5" /> Voir les enfants exclus
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 print:hidden">
                <h3 className="font-semibold text-gray-900 mb-4">Notes par semaine</h3>
                <div className="space-y-3">
                  {weeks.map((w, i) => (
                    <div key={w.semaine} className="flex items-start gap-3">
                      <span className="text-sm font-medium text-gray-500 w-10 pt-2 flex-shrink-0">{w.semaine}</span>
                      <input type="text" value={w.notes} onChange={e => updateWeekNotes(i, e.target.value)} className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500" placeholder="Notes optionnelles..." />
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Resume</h3>
                <div className="space-y-3 max-w-md">
                  <div className="flex justify-between text-sm"><span className="text-gray-600">Total tarifs locaux</span><span className="font-medium">{formatCurrency(totals.totalLocal)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-gray-600">Total tarifs sociaux locaux</span><span className="font-medium">{formatCurrency(totals.totalLocalReduced)}</span></div>
                  <div className="flex justify-between text-sm font-semibold border-t border-gray-200 pt-3"><span className="text-gray-900">Total general</span><span className="text-gray-900">{formatCurrency(totals.grandTotal)}</span></div>
                  <div className="flex justify-between items-center text-sm border-t border-gray-200 pt-3">
                    <span className="text-gray-600">Avances recues</span>
                    <div className="flex items-center gap-2">
                      <span className="print:hidden text-gray-400">-</span>
                      <input type="number" min="0" step="0.01" value={advanceAmount || ''} onChange={e => { setAdvanceAmount(parseFloat(e.target.value) || 0); setHasChanges(true) }} className="w-32 rounded-md border border-gray-300 px-2 py-1 text-sm text-right focus:ring-2 focus:ring-teal-500 focus:border-teal-500 print:border-0 print:bg-transparent" placeholder="0.00" />
                      <span className="hidden print:inline font-medium">{formatCurrency(advanceAmount)}</span>
                    </div>
                  </div>
                  <div className="flex justify-between text-base font-bold border-t-2 border-gray-300 pt-3">
                    <span className="text-gray-900">Solde a facturer</span>
                    <span className={totals.solde < 0 ? 'text-red-600' : 'text-teal-700'}>{formatCurrency(totals.solde)}</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {modalState && modalDetail && (
        <KidsListModal
          weekDetail={modalDetail}
          kind={modalState.kind}
          onClose={() => setModalState(null)}
        />
      )}
    </div>
  )
}

function BreakdownIcon({
  id, openTooltip, setOpenTooltip, tone, title, stats,
}: {
  id: string
  openTooltip: string | null
  setOpenTooltip: (v: string | null) => void
  tone: 'teal' | 'sky'
  title: string
  stats: { totalRegs: number; retained: number; noPresence: number; cancelledButPresent: number }
}) {
  const isOpen = openTooltip === id
  const toneCls = tone === 'teal' ? 'text-teal-600 hover:text-teal-800' : 'text-sky-600 hover:text-sky-800'
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpenTooltip(isOpen ? null : id)}
        onBlur={() => setTimeout(() => setOpenTooltip(prev => (prev === id ? null : prev)), 150)}
        className={`p-0.5 rounded-full transition-colors ${toneCls}`}
        title="Voir le detail"
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      {isOpen && (
        <div className="absolute z-30 left-1/2 -translate-x-1/2 top-full mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-left text-xs text-gray-700">
          <div className="font-semibold text-gray-900 mb-2">{title}</div>
          <div className="flex justify-between py-0.5"><span>Inscriptions au tarif</span><strong>{stats.totalRegs}</strong></div>
          <div className="flex justify-between py-0.5 text-green-700"><span>Retenues (au moins 1 presence)</span><strong>{stats.retained}</strong></div>
          <div className="flex justify-between py-0.5 text-red-700"><span>Sans aucune presence</span><strong>{stats.noPresence}</strong></div>
          <div className="flex justify-between py-0.5 text-amber-700 border-t border-gray-100 mt-1 pt-1"><span>Dont annulees mais presentes</span><strong>{stats.cancelledButPresent}</strong></div>
          <div className="flex justify-between py-0.5 border-t border-gray-100 mt-1 pt-1 font-semibold text-gray-900"><span>Compte final</span><strong>{stats.retained}</strong></div>
        </div>
      )}
    </span>
  )
}

function KidsListModal({
  weekDetail, kind, onClose,
}: {
  weekDetail: WeekDetail
  kind: 'counted' | 'excluded'
  onClose: () => void
}) {
  const isCounted = kind === 'counted'
  const rows = isCounted
    ? weekDetail.counted.map(c => ({ row: c, reason: null as string | null }))
    : weekDetail.excluded.map(e => ({ row: e.row, reason: e.reason }))
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            {isCounted ? <UserCheck className="h-5 w-5 text-teal-600" /> : <UserX className="h-5 w-5 text-red-600" />}
            {isCounted ? 'Enfants comptes' : 'Enfants exclus'} - Semaine {weekDetail.semaine}
            <span className="text-sm text-gray-500 font-normal">({rows.length})</span>
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4">
          {rows.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              {isCounted ? 'Aucun enfant retenu pour cette semaine.' : 'Aucun enfant exclu.'}
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Nom</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Prenom</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Tarif</th>
                  <th className="text-left px-3 py-2 font-medium text-gray-600">Ecole / CP parent</th>
                  {isCounted ? (
                    <th className="text-center px-3 py-2 font-medium text-gray-600">Jours presents</th>
                  ) : (
                    <>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">Statut</th>
                      <th className="text-left px-3 py-2 font-medium text-gray-600">Motif exclusion</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(({ row, reason }) => {
                  const school = row.schoolLabel || (row.parentCpostal ? `(autre) - CP ${row.parentCpostal}` : '(autre)')
                  const days = row.days
                  return (
                    <tr key={`${row.kidId}-${row.priceType}`}>
                      <td className="px-3 py-2 text-gray-900 font-medium uppercase">{row.nom}</td>
                      <td className="px-3 py-2 text-gray-700">{row.prenom}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${row.priceType === 'local_reduced' ? 'bg-sky-50 text-sky-700' : 'bg-teal-50 text-teal-700'}`}>
                          {row.priceType === 'local_reduced' ? 'Local reduit' : 'Local'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-700">{school}</td>
                      {isCounted ? (
                        <td className="px-3 py-2 text-center">
                          <div className="inline-flex gap-1 text-xs font-mono">
                            {(['L','M','Me','J','V'] as const).map(k => (
                              <span key={k} className={`w-6 h-6 inline-flex items-center justify-center rounded ${days[k] ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-400'}`}>{k}</span>
                            ))}
                          </div>
                        </td>
                      ) : (
                        <>
                          <td className="px-3 py-2 text-gray-700">{row.cancellationStatus === 'none' || !row.cancellationStatus ? 'Active' : row.cancellationStatus}</td>
                          <td className="px-3 py-2 text-red-700 text-xs">{reason}</td>
                        </>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="px-6 py-3 border-t border-gray-200 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-800 rounded-lg hover:bg-gray-200">Fermer</button>
        </div>
      </div>
    </div>
  )
}
